// TransformationLayer/TransformationPlayerVanilla.js

/**
 * Vanilla JS Transformation Player
 * Takes template, sampleResponse, transformationSteps and renders the transformed output
 * No React dependencies - pure vanilla JS
 * 
 * UPDATED: Supports multi-select styles with operations array
 * UPDATED: Supports alignment (text-left, text-center, text-right) on .row elements
 * UPDATED: Properly handles style_meta metadata
 * UPDATED: Properly renders buttons and button-groups
 */

import nunjucks from "nunjucks/browser/nunjucks";
import {
  applyMediaTransformation,
  generateTableHTML,
  generateCardHTML,
  generateListHTML,
} from "../../PreviewTesterHome/PreviewTester/utils/transformationRules";

import {
  wrapFieldValuesWithSpans,

  applyTransformationLogic,

  isImageUrl,
  parseRowsToData,

} from "../../PreviewTesterHome/PreviewTester/utils/renderingUtils";

import { enhanceTemplateWithLoopWrappers } from "../../PreviewTesterHome/PreviewTester/utils/templateEnhancer";

// ============================================================
// SAFE LOGGING UTILITY
// ============================================================
const safeStringify = (obj, maxDepth = 3) => {
  if (obj === null || obj === undefined) return String(obj);
  if (obj instanceof HTMLElement || obj instanceof Node) {
    return `[DOM Element: ${obj.tagName || 'unknown'}]`;
  }
  if (typeof obj === 'function') {
    return `[Function: ${obj.name || 'anonymous'}]`;
  }
  if (typeof obj !== 'object') return String(obj);
  if (obj instanceof Date) return obj.toISOString();
  if (Array.isArray(obj)) {
    if (obj.length > 10) return `[Array(${obj.length})]`;
    return obj.map(item => safeStringify(item, maxDepth - 1));
  }

  const seen = new WeakSet();
  const safeSerialize = (obj, depth) => {
    if (depth <= 0) return '[Max Depth Reached]';
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (seen.has(obj)) return '[Circular Reference]';
    if (obj instanceof HTMLElement || obj instanceof Node) {
      return `[DOM Element: ${obj.tagName || 'unknown'}]`;
    }
    if (obj instanceof Date) return obj.toISOString();
    seen.add(obj);
    const result = {};
    const keys = Object.keys(obj);
    const limitedKeys = keys.slice(0, 20);
    for (const key of limitedKeys) {
      try {
        result[key] = safeSerialize(obj[key], depth - 1);
      } catch (e) {
        result[key] = '[Error: ' + e.message + ']';
      }
    }
    if (keys.length > 20) {
      result['_truncated'] = `(${keys.length - 20} more keys)`;
    }
    return result;
  };

  try {
    return safeSerialize(obj, maxDepth);
  } catch (e) {
    return `[Serialization Error: ${e.message}]`;
  }
};

// ============================================================
// SELECTOR HELPERS - Get paths from both old and new formats
// ============================================================
const getSelectorPath = (selector, type = 'start') => {
  if (!selector) return '';
  if (typeof selector === 'string') return selector;
  if (typeof selector === 'object') {
    if (type === 'start' && selector.start && typeof selector.start === 'string') {
      return selector.start;
    }
    if (type === 'end' && selector.end && typeof selector.end === 'string') {
      return selector.end;
    }
    if (type === 'loopSelector' && selector.loopSelector && typeof selector.loopSelector === 'string') {
      return selector.loopSelector;
    }
    if (type === 'start' && selector.start && selector.start.elementPath) {
      return selector.start.elementPath;
    }
    if (type === 'end' && selector.end && selector.end.elementPath) {
      return selector.end.elementPath;
    }
    if (type === 'loopSelector' && selector.loopSelector) {
      return selector.loopSelector;
    }
  }
  return '';
};

// ============================================================
// SELECTOR HELPERS - Normalize selector to new format
// ============================================================
const normalizeSelector = (selector) => {
  if (!selector) return null;
  if (typeof selector.start === 'string' && typeof selector.end === 'string') {
    return {
      start: selector.start,
      end: selector.end,
      ...(selector.loopSelector ? { loopSelector: selector.loopSelector } : {})
    };
  }
  const startPath = getSelectorPath(selector, 'start');
  const endPath = getSelectorPath(selector, 'end');
  const loopSelector = getSelectorPath(selector, 'loopSelector');
  return {
    start: startPath,
    end: endPath,
    ...(loopSelector ? { loopSelector: loopSelector } : {})
  };
};

// ============================================================
// HELPER: Get nth-child path relative to a container
// ============================================================
const getNthChildPathLocal = (element, container) => {
  if (!element || !container) return "";
  if (element === container) return "";

  const path = [];
  let current = element;
  let depth = 0;

  while (current && current !== container) {
    if (current.parentElement) {
      const allChildren = Array.from(current.parentElement.children);
      const index = allChildren.indexOf(current);
      if (index >= 0) {
        const tagName = current.tagName.toLowerCase();
        const part = `${tagName}:nth-child(${index + 1})`;
        path.unshift(part);
      }
    }
    current = current.parentElement;
    depth++;
    if (depth > 20) break;
  }

  return path.join(" > ");
};

// ============================================================
// HELPER: Get relative path between two elements
// ============================================================
const getRelativePathLocal = (targetElement, container) => {
  if (!targetElement || !container) return "";
  if (targetElement === container) return "";

  const path = [];
  let current = targetElement;
  let depth = 0;

  while (current && current !== container) {
    if (current.parentElement) {
      const parent = current.parentElement;
      const allChildren = Array.from(parent.children);
      const index = allChildren.indexOf(current);

      if (index >= 0) {
        const tagName = current.tagName.toLowerCase();
        const nthChild = index + 1;
        const part = `${tagName}:nth-child(${nthChild})`;
        path.unshift(part);
      }
    } else {
      break;
    }

    current = current.parentElement;
    depth++;
    if (depth > 20) break;
  }

  return path.join(" > ");
};

// ============================================================
// FIND ELEMENT BY PATH
// ============================================================
const findElementByPathLocal = (container, path) => {
  if (!container || !path) return null;
  if (!path.trim()) return container;

  const parts = path.split(" > ");
  let current = container;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const match = part.match(/^([a-zA-Z]+):nth-child\((\d+)\)$/);

    if (!match) return null;

    const tagName = match[1];
    const nthChild = parseInt(match[2]);

    let children = Array.from(current.children).filter(
      (el) => el.tagName.toLowerCase() === tagName,
    );

    if (nthChild > 0 && nthChild <= children.length) {
      current = children[nthChild - 1];
    } else {
      return null;
    }
  }

  return current;
};

// ============================================================
// HELPER: Apply alignment to rows
// ============================================================
const applyAlignmentToRows = (doc, startElement, endElement, container, alignment) => {
  if (!alignment || alignment === 'left') return 0;

  const alignmentClass = `text-${alignment}`;
  console.log(`[TransformationPlayer] 📐 Applying alignment: ${alignment} (class: ${alignmentClass})`);

  // Get all elements between start and end
  const elementsBetween = getElementsBetweenLocal(startElement, endElement, container);
  if (elementsBetween.length === 0) return 0;

  // Collect unique rows
  const affectedRows = new Set();
  for (const element of elementsBetween) {
    if (element === container || element.parentElement === container) continue;
    if (element.classList && element.classList.contains('empty-line')) continue;

    const row = element.closest?.('.row') || element.parentElement?.closest?.('.row');
    if (row) {
      affectedRows.add(row);
    }
  }

  // If no rows found via closest, try direct row search
  if (affectedRows.size === 0) {
    const allRows = container.querySelectorAll('.row');
    for (const row of allRows) {
      const rowInRange = elementsBetween.some(el => row.contains(el) || el === row);
      if (rowInRange) {
        affectedRows.add(row);
      }
    }
  }

  // Apply alignment class to each row
  let modifiedCount = 0;
  for (const row of affectedRows) {
    const existingClass = row.className || '';
    const classesWithoutAlignment = existingClass
      .split(' ')
      .filter(c => c && !c.startsWith('text-'));
    classesWithoutAlignment.push(alignmentClass);
    row.className = classesWithoutAlignment.join(' ');
    modifiedCount++;
    console.log(`[TransformationPlayer] ✅ Applied ${alignmentClass} to row`);
  }

  return modifiedCount;
};

// ============================================================
// HELPER: getElementsBetween
// ============================================================
const getElementsBetweenLocal = (startElement, endElement, container) => {
  if (!startElement || !endElement || !container) return [];

  const allElements = [];
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        if (node === container) return NodeFilter.FILTER_REJECT;
        if (node.tagName === 'DIV' && !node.classList.contains('item-ui-screen') && node.children.length > 0) {
          const hasDirectText = Array.from(node.childNodes).some(c => c.nodeType === 3 && c.textContent?.trim());
          if (!hasDirectText) return NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );

  let node = walker.nextNode();
  while (node) {
    allElements.push(node);
    node = walker.nextNode();
  }

  let startIndex = -1;
  let endIndex = -1;

  for (let i = 0; i < allElements.length; i++) {
    if (allElements[i] === startElement) startIndex = i;
    if (allElements[i] === endElement) endIndex = i;
    if (startIndex !== -1 && endIndex !== -1) break;
  }

  if (startIndex === -1 || endIndex === -1) return [];

  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  return allElements.slice(minIndex, maxIndex + 1);
};

class TransformationPlayer {
  constructor(options = {}) {
    console.log('[TransformationPlayer] constructor called');
    console.log('[TransformationPlayer] template length:', options.template?.length || 0);
    console.log('[TransformationPlayer] transformationSteps count:', options.transformationSteps?.length || 0);

    this.template = options.template || '';
    this.sampleResponse = options.sampleResponse || {};
    this.transformationSteps = options.transformationSteps || [];
    this.targetElement = options.targetElement || null;
    this.onStepComplete = options.onStepComplete || null;
    this.onError = options.onError || null;

    this.currentData = null;
    this.renderedOutput = '';
    this.stepResults = [];
    this.currentStepIndex = 0;
    this.isPlaying = false;
    this.error = null;
    this.originalRenderedOutput = '';
    this.isSampleArray = false;
    this.caseType = 'unknown';
    this.caseDescription = '';
    this._nunjucksEnv = null;

    // Bind methods
    this.playAll = this.playAll.bind(this);
    this.playNext = this.playNext.bind(this);
    this.reset = this.reset.bind(this);
    this.render = this.render.bind(this);
    this.applyStep = this.applyStep.bind(this);
    this.init = this.init.bind(this);
    this.renderTemplate = this.renderTemplate.bind(this);
    this.setError = this.setError.bind(this);
    this.destroy = this.destroy.bind(this);
    this.update = this.update.bind(this);
    this.detectCaseType = this.detectCaseType.bind(this);
    this.getCaseDescription = this.getCaseDescription.bind(this);
    this.getNunjucksEnv = this.getNunjucksEnv.bind(this);
    this.getSelectorsFromStep = this.getSelectorsFromStep.bind(this);
    this.getElementsBetween = this.getElementsBetween.bind(this);
    this.applyStyleTransformationStep = this.applyStyleTransformationStep.bind(this);
    this.applyStructuralTransformationStep = this.applyStructuralTransformationStep.bind(this);
    this.applyMediaTransformationStep = this.applyMediaTransformationStep.bind(this);
    this.applyButtonTransformationStep = this.applyButtonTransformationStep.bind(this);
    this.applyButtonGroupTransformationStep = this.applyButtonGroupTransformationStep.bind(this);

    // Initialize
    if (this.targetElement) {
      this.init();
    }
  }

  // ============================================================
  // GET SELECTORS FROM STEP (handles both old and new formats)
  // ============================================================
  getSelectorsFromStep(step) {
    if (step.selectors && Array.isArray(step.selectors) && step.selectors.length > 0) {
      console.log('[TransformationPlayer] ✅ Using selectors array with', step.selectors.length, 'items');
      return step.selectors.map(s => normalizeSelector(s)).filter(s => s && s.start);
    } else if (step.selector) {
      console.log('[TransformationPlayer] 🔄 Converting legacy selector to array');
      const normalized = normalizeSelector(step.selector);
      return normalized ? [normalized] : [];
    } else {
      console.warn('[TransformationPlayer] ⚠️ No selectors found in step:', step.id);
      return [];
    }
  }

  // ============================================================
  // CHECK STEP TYPE HELPERS
  // ============================================================
  isButtonStep(step) {
    return step.type === 'button' || (step.isButtonTransformation === true);
  }

  isButtonGroupStep(step) {
    return step.type === 'button-group' || (step.isButtonGroupTransformation === true);
  }

  isStyleStep(step) {
    return step.type === 'style' || step.type === 'single';
  }

  isStructuralStep(step) {
    return step.type === 'structural' || step.type === 'multiple';
  }

  isMediaStep(step) {
    return step.type === 'media';
  }

  isCombinedStep(step) {
    return step.type === 'combined';
  }

  getNunjucksEnv() {
    if (!this._nunjucksEnv) {
      this._nunjucksEnv = nunjucks.configure({ autoescape: false });
      this._nunjucksEnv.addFilter("wrapLoopContent", function (content, varName) {
        if (!content) return content;
        const trimmed = content.trim();
        if (!trimmed) return content;
        return `<span class="for-loop-started" data-loop-type="inner" data-loop-var="${varName}">${trimmed}</span>`;
      });
      this._nunjucksEnv.addFilter("wrapOuterLoopContent", function (content, varName) {
        if (!content) return content;
        return content;
      });
      console.log('[TransformationPlayer] Nunjucks environment initialized');
    }
    return this._nunjucksEnv;
  }

  // ============================================================
  // DETECT CASE TYPE - FIXED with template analysis
  // ============================================================
  detectCaseType(data) {
    console.log('[TransformationPlayer] detectCaseType called');

    // STEP 1: Check if data is a root array (Case 1)
    if (Array.isArray(data)) {
      console.log('[TransformationPlayer] ✅ Detected Case 1: Root Array');
      return 'case1';
    }

    // STEP 2: Analyze template structure
    const templateLines = this.template.split('\n');
    let hasContentBeforeLoop = false;
    let hasContentAfterLoop = false;
    let foundForLoop = false;
    let loopDepth = 0;
    let outerLoopStartLine = -1;
    let outerLoopEndLine = -1;

    for (let i = 0; i < templateLines.length; i++) {
      const trimmed = templateLines[i].trim();
      if (!trimmed || trimmed.startsWith('{#') || trimmed.startsWith('{% comment')) continue;

      const isForLoopStart = /{%\s*for\s+/.test(trimmed);
      const isForLoopEnd = /{%\s*endfor\s*%}/.test(trimmed);

      if (isForLoopStart) {
        foundForLoop = true;
        loopDepth++;
        if (outerLoopStartLine === -1) {
          outerLoopStartLine = i;
        }
        continue;
      }

      if (isForLoopEnd) {
        loopDepth--;
        if (loopDepth === 0 && outerLoopEndLine === -1) {
          outerLoopEndLine = i;
        }
        continue;
      }

      // Check for content BEFORE the first loop
      if (!foundForLoop && loopDepth === 0) {
        const isTemplateTag = trimmed.startsWith('{%') || trimmed.startsWith('{#');
        if (!isTemplateTag && trimmed.length > 0) {
          hasContentBeforeLoop = true;
        }
      }

      // Check for content AFTER the first loop
      if (foundForLoop && loopDepth === 0 && outerLoopEndLine !== -1 && i > outerLoopEndLine) {
        const isTemplateTag = trimmed.startsWith('{%') || trimmed.startsWith('{#');
        if (!isTemplateTag && trimmed.length > 0) {
          hasContentAfterLoop = true;
        }
      }
    }

    console.log('[TransformationPlayer] 🔍 Template analysis:', {
      foundForLoop,
      hasContentBeforeLoop,
      hasContentAfterLoop,
      loopDepth,
      outerLoopStartLine,
      outerLoopEndLine
    });

    // STEP 3: Determine case based on template structure
    // Case 2: Single screen with metadata - content exists BEFORE or AFTER the loop
    if (foundForLoop && (hasContentBeforeLoop || hasContentAfterLoop)) {
      console.log('[TransformationPlayer] ✅ Detected Case 2: Single screen with metadata');
      this.caseDescription = 'Single Object → Single screen, outer loop IS wrapped';
      return 'case2';
    }

    // Case 3: Multiple screens from nested array - NO content before or after the loop
    if (foundForLoop && !hasContentBeforeLoop && !hasContentAfterLoop) {
      if (typeof data === 'object' && data !== null) {
        let nestedArrayKey = null;
        for (const key of Object.keys(data)) {
          if (Array.isArray(data[key]) && data[key].length > 0) {
            nestedArrayKey = key;
            break;
          }
        }
        if (nestedArrayKey) {
          console.log(`[TransformationPlayer] ✅ Detected Case 3: Nested array "${nestedArrayKey}" with no surrounding content`);
          this.caseDescription = 'Object with Nested Array → Multiple screens, outer loop NOT wrapped';
          return 'case3';
        }
      }
    }

    // If no loop found
    if (!foundForLoop) {
      console.log('[TransformationPlayer] ℹ️ No loop detected, treating as single screen');
      this.caseDescription = 'No loop → Single screen';
      return 'case2';
    }

    // Fallback
    console.log('[TransformationPlayer] ⚠️ Unknown case type, defaulting to case2');
    this.caseDescription = 'Unknown case type, defaulting to single screen';
    return 'case2';
  }

  getCaseDescription(caseType) {
    const descriptions = {
      'case1': 'Root Array → Multiple screens, outer loop NOT wrapped',
      'case2': 'Single Object → Single screen, outer loop IS wrapped (if loop present)',
      'case3': 'Object with Nested Array → Multiple screens, outer loop NOT wrapped',
      'unknown': 'Unknown case type'
    };
    return descriptions[caseType] || descriptions['unknown'];
  }

  init() {
    console.log('[TransformationPlayer] init() called');
    try {
      const parsed = typeof this.sampleResponse === 'string'
        ? JSON.parse(this.sampleResponse)
        : this.sampleResponse;
      this.currentData = parsed;
      this.isSampleArray = Array.isArray(parsed);
      this.caseType = this.detectCaseType(parsed);
      this.caseDescription = this.getCaseDescription(this.caseType);
      console.log('[TransformationPlayer] 📊 Case Type:', this.caseType);
      console.log('[TransformationPlayer] 📝 Case Description:', this.caseDescription);
      this.renderedOutput = this.renderTemplate(parsed);
      this.originalRenderedOutput = this.renderedOutput;
      this.render();
      console.log('[TransformationPlayer] init() completed');
    } catch (err) {
      console.log('[TransformationPlayer] ❌ init() error:', err.message);
      this.setError(`Failed to parse sample data: ${err.message}`);
    }
  }

  // ============================================================
  // PARSE TEMPLATE LINES - Detect buttons and button groups
  // ============================================================
  parseTemplateLines(template) {
    const lines = template.split('\n');
    return lines.map((line, index) => {
      const trimmed = line.trim();

      // Detect button: exact match for "A Regular Btn"
      const isButton = trimmed === 'A Regular Btn';

      // Detect button group: starts with "Button Group" followed by optional number
      const isButtonGroup = /^Button Group\d*$/.test(trimmed);

      // Detect button from pattern: [Button] Text or Button: Text
      const isButtonPattern = /^\[Button\]\s*(.+)$/.test(trimmed) || /^Button:\s*(.+)$/.test(trimmed);

      // Detect button group from pattern
      const isButtonGroupPattern = /^\[Button Group\]\s*(.+)$/.test(trimmed) || /^Button Group\s*(.+)$/.test(trimmed);

      let type = 'text';
      let isButtonFinal = false;
      let isButtonGroupFinal = false;
      let buttons = null;
      let buttonText = null;

      if (isButton || isButtonPattern) {
        type = 'button';
        isButtonFinal = true;
        if (isButtonPattern) {
          const match = trimmed.match(/^\[Button\]\s*(.+)$/) || trimmed.match(/^Button:\s*(.+)$/);
          buttonText = match ? match[1].trim() : trimmed;
        } else {
          buttonText = trimmed;
        }
      } else if (isButtonGroup || isButtonGroupPattern) {
        type = 'button-group';
        isButtonGroupFinal = true;
        // Extract group name and parse buttons
        let groupName = trimmed;
        if (isButtonGroupPattern) {
          const match = trimmed.match(/^\[Button Group\]\s*(.+)$/) || trimmed.match(/^Button Group\s*(.+)$/);
          groupName = match ? match[1].trim() : trimmed;
        }
        // Parse button names from the group (comma separated)
        const buttonNames = groupName.split(',').map(b => b.trim()).filter(b => b);
        buttons = buttonNames.length > 0 ? buttonNames : ['Button 1', 'Button 2'];
      }

      return {
        id: `line-${index}`,
        content: line,
        trimmed: trimmed,
        type: type,
        isButton: isButtonFinal,
        isButtonGroup: isButtonGroupFinal,
        indent: line.match(/^(\s*)/)?.[1] || '',
        depth: 0,
        isCustom: false,
        isLoopStart: false,
        isLoopEnd: false,
        loopVariable: null,
        loopArray: null,
        condition: null,
        variables: [],
        isInForLoop: false,
        isInIfBlock: false,
        originalIndex: -1,
        customId: null,
        customMessage: null,
        buttons: buttons,
        buttonText: buttonText,
        _sourceType: type,
      };
    });
  }

  // ============================================================
  // RENDER TEMPLATE - FIXED with proper button/button-group rendering
  // ============================================================
  // TransformationLayer/TransformationPlayerVanilla.js - Fixed renderTemplate
  // TransformationLayer/TransformationPlayerVanilla.js - Fixed renderTemplate

  renderTemplate(data) {
    console.log('[TransformationPlayer] renderTemplate() called');
    const nunjucksEnv = this.getNunjucksEnv();

    try {
      const enhancedTemplate = enhanceTemplateWithLoopWrappers(this.template);
      console.log('[TransformationPlayer] ✅ Template enhanced with loop wrappers');

      let parsed = data;
      let originalIsArray = false;
      let shouldTreatAsArray = false;
      let nestedArrayKey = null;
      let effectiveArrayData = null;
      let isCase3WithMetadata = false;

      if (Array.isArray(parsed)) {
        originalIsArray = true;
        if (parsed.length === 0) {
          this.setError('Sample response is an empty array.');
          return '';
        }
        effectiveArrayData = parsed;
        console.log(`[TransformationPlayer] Root array with ${parsed.length} items`);
      }

      // Analyze template structure
      const templateLinesArr = this.template.split('\n');
      let hasContentBeforeLoop = false;
      let hasContentAfterLoop = false;
      let foundForLoop = false;
      let forLoopDepth = 0;
      let outerLoopStartLine = -1;
      let outerLoopEndLine = -1;
      let loopVarName = 'item';
      let loopIterable = 'response_data';

      for (let i = 0; i < templateLinesArr.length; i++) {
        const trimmed = templateLinesArr[i].trim();
        if (!trimmed || trimmed.startsWith('{#') || trimmed.startsWith('{% comment %')) continue;

        const isForLoopStart = /{%\s*for\s+([a-zA-Z_]+)\s+in\s+([^%]+)\s*%}/.test(trimmed);
        const isForLoopEnd = /{%\s*endfor\s*%}/.test(trimmed);

        if (isForLoopStart) {
          foundForLoop = true;
          forLoopDepth++;
          if (outerLoopStartLine === -1) {
            outerLoopStartLine = i;
            const match = trimmed.match(/{%\s*for\s+([a-zA-Z_]+)\s+in\s+([^%]+)\s*%}/);
            if (match) {
              loopVarName = match[1];
              loopIterable = match[2].trim();
            }
          }
          continue;
        }

        if (isForLoopEnd) {
          forLoopDepth--;
          if (forLoopDepth === 0 && outerLoopEndLine === -1) {
            outerLoopEndLine = i;
          }
          continue;
        }

        if (!foundForLoop && forLoopDepth === 0) {
          const isTemplateTag = trimmed.startsWith('{%') || trimmed.startsWith('{#');
          if (!isTemplateTag && trimmed.length > 0) {
            hasContentBeforeLoop = true;
          }
        }

        if (foundForLoop && forLoopDepth === 0 && outerLoopEndLine !== -1 && i > outerLoopEndLine) {
          const isTemplateTag = trimmed.startsWith('{%') || trimmed.startsWith('{#');
          if (!isTemplateTag && trimmed.length > 0) {
            hasContentAfterLoop = true;
          }
        }
      }

      // Determine case
      if (originalIsArray && Array.isArray(parsed)) {
        shouldTreatAsArray = true;
        effectiveArrayData = parsed;
        console.log('[TransformationPlayer] 📊 Case 1: Root array detected');
      } else if (foundForLoop && !hasContentBeforeLoop && !hasContentAfterLoop) {
        let foundNestedArray = false;
        if (typeof parsed === 'object' && parsed !== null) {
          for (const key of Object.keys(parsed)) {
            if (Array.isArray(parsed[key]) && parsed[key].length > 0) {
              shouldTreatAsArray = true;
              nestedArrayKey = key;
              effectiveArrayData = parsed[key];
              foundNestedArray = true;
              const otherKeys = Object.keys(parsed).filter((k) => k !== key);
              isCase3WithMetadata = otherKeys.length > 0;
              console.log(`[TransformationPlayer] 📊 Case 3: Nested array "${key}" with ${parsed[key].length} items`);
              if (isCase3WithMetadata) {
                console.log(`[TransformationPlayer] 📊 Case 3 with metadata: ${otherKeys.join(', ')}`);
              }
              break;
            }
          }
        }
        if (!foundNestedArray) {
          shouldTreatAsArray = false;
          effectiveArrayData = [parsed];
          console.log('[TransformationPlayer] 📊 Single object with loop');
        }
      } else if (foundForLoop && (hasContentBeforeLoop || hasContentAfterLoop)) {
        shouldTreatAsArray = false;
        console.log('[TransformationPlayer] 📊 Case 2: Single screen with metadata');
      } else {
        shouldTreatAsArray = false;
        console.log('[TransformationPlayer] 📊 No loop detected');
      }

      let renderedHTML = '';

      // ============================================================
      // CASE 1/3: Multiple screens
      // ============================================================
      if (shouldTreatAsArray && effectiveArrayData && Array.isArray(effectiveArrayData) && effectiveArrayData.length > 0) {
        console.log(`[TransformationPlayer] 📊 Rendering ${effectiveArrayData.length} items (Case 1/3)`);

        const enhancedLines = enhancedTemplate.split('\n');
        let outerLoopStartLine2 = -1;
        let outerLoopEndLine2 = -1;
        let loopDepth = 0;
        let loopVarName2 = 'item';
        let loopIterable2 = 'response_data';

        for (let i = 0; i < enhancedLines.length; i++) {
          const trimmed = enhancedLines[i].trim();
          const isForLoopStart = /{%\s*for\s+([a-zA-Z_]+)\s+in\s+([^%]+)\s*%}/.test(trimmed);
          const isForLoopEnd = /{%\s*endfor\s*%}/.test(trimmed);

          if (isForLoopStart && outerLoopStartLine2 === -1) {
            outerLoopStartLine2 = i;
            const match = trimmed.match(/{%\s*for\s+([a-zA-Z_]+)\s+in\s+([^%]+)\s*%}/);
            if (match) {
              loopVarName2 = match[1];
              loopIterable2 = match[2].trim();
            }
            loopDepth++;
            continue;
          }

          if (isForLoopEnd && loopDepth > 0) {
            loopDepth--;
            if (loopDepth === 0 && outerLoopEndLine2 === -1) {
              outerLoopEndLine2 = i;
            }
            continue;
          }
        }

        let loopBody = '';
        if (outerLoopStartLine2 !== -1 && outerLoopEndLine2 !== -1 && outerLoopStartLine2 < outerLoopEndLine2) {
          loopBody = enhancedLines.slice(outerLoopStartLine2 + 1, outerLoopEndLine2).join('\n');
          console.log('[TransformationPlayer] 📊 Enhanced loop body extracted, length:', loopBody.length);
        } else {
          loopBody = enhancedTemplate
            .replace(/{%\s*for\s+[a-zA-Z_]+\s+in\s+[^%]+\s*%}/g, '')
            .replace(/{%\s*endfor\s*%}/g, '');
          console.log('[TransformationPlayer] 📊 Using fallback loop body');
        }

        const hasInnerLoop = /{%\s*for\s+[a-zA-Z_]+\s+in\s+/.test(loopBody);
        console.log(`[TransformationPlayer] 📊 Has inner loop: ${hasInnerLoop}`);

        const screenHTMLs = [];
        const iterablePath = loopIterable2.split('.');

        for (let index = 0; index < effectiveArrayData.length; index++) {
          const item = effectiveArrayData[index];

          let itemContext = {};
          let itemRendered = '';
          let renderMethod = '';

          if (hasInnerLoop) {
            if (iterablePath.length === 1) {
              itemContext = { response_data: [item] };
            } else if (iterablePath.length === 2 && iterablePath[0] === 'response_data') {
              const arrayKey = iterablePath[1];
              if (isCase3WithMetadata) {
                itemContext = {
                  response_data: {
                    ...parsed,
                    [arrayKey]: [item],
                  },
                };
              } else {
                itemContext = {
                  response_data: {
                    [arrayKey]: [item],
                  },
                };
              }
            } else {
              itemContext = { response_data: [item] };
            }
            console.log('[TransformationPlayer] 📊 Has inner loop - using response_data context');
          } else {
            const loopVar = loopVarName2 || 'item';
            itemContext = { [loopVar]: item };
            console.log(`[TransformationPlayer] 📊 No inner loop - using direct ${loopVar} context`);
          }

          if (loopBody && loopBody.trim()) {
            try {
              itemRendered = nunjucksEnv.renderString(loopBody, itemContext);
              if (itemRendered && typeof itemRendered === 'string') {
                itemRendered = itemRendered.replace(/&gt;/g, '>');
                itemRendered = itemRendered.replace(/&lt;/g, '<');
                itemRendered = itemRendered.replace(/&amp;/g, '&');
                itemRendered = itemRendered.replace(/&quot;/g, '"');
              }
              if (itemRendered && itemRendered.trim()) {
                renderMethod = 'loopBody';
                console.log(`[TransformationPlayer] ✅ Item ${index} rendered (loop body), length: ${itemRendered.length}`);
              } else {
                throw new Error('Empty result');
              }
            } catch (err) {
              console.warn('[TransformationPlayer] ⚠️ Loop body render failed:', err.message);
            }
          }

          if (!itemRendered || !itemRendered.trim()) {
            try {
              let fallbackContext = {};
              if (iterablePath.length === 1) {
                fallbackContext = { response_data: [item] };
              } else if (iterablePath.length === 2 && iterablePath[0] === 'response_data') {
                const arrayKey = iterablePath[1];
                if (isCase3WithMetadata) {
                  fallbackContext = {
                    response_data: {
                      ...parsed,
                      [arrayKey]: [item],
                    },
                  };
                } else {
                  fallbackContext = {
                    response_data: {
                      [arrayKey]: [item],
                    },
                  };
                }
              } else {
                fallbackContext = { response_data: [item] };
              }

              itemRendered = nunjucksEnv.renderString(enhancedTemplate, fallbackContext);
              if (itemRendered && typeof itemRendered === 'string') {
                itemRendered = itemRendered.replace(/&gt;/g, '>');
                itemRendered = itemRendered.replace(/&lt;/g, '<');
                itemRendered = itemRendered.replace(/&amp;/g, '&');
                itemRendered = itemRendered.replace(/&quot;/g, '"');
              }
              if (itemRendered && itemRendered.trim()) {
                renderMethod = 'fullTemplate';
                console.log(`[TransformationPlayer] ✅ Item ${index} rendered (full template fallback), length: ${itemRendered.length}`);
              } else {
                throw new Error('Empty result');
              }
            } catch (err) {
              console.warn('[TransformationPlayer] ⚠️ Full template render failed:', err.message);
              itemRendered = JSON.stringify(item, null, 2);
              renderMethod = 'json';
              console.log(`[TransformationPlayer] 📊 Item ${index} rendered as JSON (final fallback)`);
            }
          }

          // ✅ Pass through wrapFieldValuesWithSpans - it will handle key/value/sep architecture
          // But we should NOT wrap buttons here - they'll be handled by transformation steps
          const wrappedItem = wrapFieldValuesWithSpans(
            itemRendered,
            [],  // No template lines needed for initial render
            true,
            true
          );

          const screenHTML = `<div class="item-ui-screen" data-item-ui-index="${index}"><div><div class="contentWraps">${wrappedItem}</div></div></div>`;
          screenHTMLs.push(screenHTML);
        }

        renderedHTML = screenHTMLs.join('\n');
        console.log(`[TransformationPlayer] ✅ Generated ${screenHTMLs.length} screens`);
      } else {
        // ============================================================
        // CASE 2: Single screen
        // ============================================================
        console.log('[TransformationPlayer] 📊 Single screen rendering (Case 2)');
        const context = { response_data: parsed };
        let fullRendered = nunjucksEnv.renderString(enhancedTemplate, context);

        if (fullRendered && typeof fullRendered === 'string') {
          fullRendered = fullRendered.replace(/&gt;/g, '>');
          fullRendered = fullRendered.replace(/&lt;/g, '<');
          fullRendered = fullRendered.replace(/&amp;/g, '&');
          fullRendered = fullRendered.replace(/&quot;/g, '"');
        }

        // ✅ Pass through wrapFieldValuesWithSpans - it will handle key/value/sep architecture
        // But we should NOT wrap buttons here - they'll be handled by transformation steps
        const wrappedContent = wrapFieldValuesWithSpans(
          fullRendered,
          [],  // No template lines needed for initial render
          false,
          false
        );

        renderedHTML = `<div class="item-ui-screen" data-item-ui-index="0"><div><div class="contentWraps">${wrappedContent}</div></div></div>`;
      }

      if (renderedHTML && typeof renderedHTML === 'string') {
        renderedHTML = renderedHTML.replace(/&gt;/g, '>');
        renderedHTML = renderedHTML.replace(/&lt;/g, '<');
        renderedHTML = renderedHTML.replace(/&amp;/g, '&');
        renderedHTML = renderedHTML.replace(/&quot;/g, '"');
      }

      return renderedHTML;
    } catch (err) {
      console.log('[TransformationPlayer] ❌ renderTemplate error:', err.message);
      this.setError(`Template render error: ${err.message}`);
      return '';
    }
  }

  // ============================================================
  // HELPER: getElementsBetween
  // ============================================================
  getElementsBetween(startElement, endElement, container) {
    return getElementsBetweenLocal(startElement, endElement, container);
  }

  // ============================================================
  // APPLY BUTTON TRANSFORMATION
  // ============================================================
  // ============================================================
  // APPLY BUTTON TRANSFORMATION - Actually renders the button
  // ============================================================
  applyButtonTransformationStep(step) {
    console.log('[🔘 BUTTON] applyButtonTransformationStep START');
    console.log('[🔘 BUTTON] buttonText:', step.buttonText);

    // Find the button text in the rendered output and wrap it with button styling
    const buttonText = step.buttonText || 'Button';
    const buttonHtml = `<div class="row button" data-template-type="button" data-template-id="${step.id}">${buttonText}</div>`;

    // Replace the plain text with the button HTML
    // Look for the exact button text in the rendered output
    const escapedText = buttonText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`<div class="row">\\s*${escapedText}\\s*</div>`, 'g');

    if (regex.test(this.renderedOutput)) {
      this.renderedOutput = this.renderedOutput.replace(regex, buttonHtml);
      console.log('[🔘 BUTTON] ✅ Replaced button text with button HTML');
      return true;
    }

    // Fallback: try to find just the text
    if (this.renderedOutput.includes(buttonText)) {
      this.renderedOutput = this.renderedOutput.replace(buttonText, buttonHtml);
      console.log('[🔘 BUTTON] ✅ Replaced button text (fallback)');
      return true;
    }

    console.log('[🔘 BUTTON] ⚠️ Could not find button text in rendered output');
    return true; // Still return true to not break the flow
  }

  // ============================================================
  // APPLY BUTTON GROUP TRANSFORMATION - Actually renders the button group
  // ============================================================
  // ============================================================
  // APPLY BUTTON GROUP TRANSFORMATION - FIXED to properly find and replace
  // ============================================================
  applyButtonGroupTransformationStep(step) {
    console.log('[📦 BUTTON GROUP] applyButtonGroupTransformationStep START');
    console.log('[📦 BUTTON GROUP] groupName:', step.groupName);
    console.log('[📦 BUTTON GROUP] buttons:', step.buttons);

    const groupName = step.groupName || 'Button Group';
    const buttons = step.buttons || ['Button 1', 'Button 2'];

    // Build the button group HTML
    const buttonHtml = buttons.map((btn) => {
      const text = typeof btn === 'string' ? btn : (btn.text || String(btn));
      return `<button class="button-cell" style="padding:6px 16px;background:#4299e1;color:white;border:none;border-radius:4px;font-size:12px;cursor:default;margin:2px;">${text}</button>`;
    }).join('');

    const groupHtml = `<div class="row button-group" data-template-type="button-group" data-template-id="${step.id}" style="display:flex;flex-wrap:wrap;gap:6px;padding:4px 0;align-items:center;">${buttonHtml}</div>`;

    console.log('[📦 BUTTON GROUP] groupHtml:', groupHtml);

    // ============================================================
    // TRY MULTIPLE APPROACHES TO FIND AND REPLACE
    // ============================================================

    // Approach 1: Look for the exact pattern with the group name
    // Pattern: <div class="row">Button Group1</div>
    const escapedGroupName = groupName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex1 = new RegExp(`<div class="row">\\s*${escapedGroupName}\\s*</div>`, 'g');

    if (regex1.test(this.renderedOutput)) {
      this.renderedOutput = this.renderedOutput.replace(regex1, groupHtml);
      console.log('[📦 BUTTON GROUP] ✅ Replaced using Approach 1');
      return true;
    }

    // Approach 2: Look for just the group name as a line
    // Pattern: Button Group1 (as a whole line)
    const regex2 = new RegExp(`^\\s*${escapedGroupName}\\s*$`, 'gm');
    if (regex2.test(this.renderedOutput)) {
      this.renderedOutput = this.renderedOutput.replace(regex2, groupHtml);
      console.log('[📦 BUTTON GROUP] ✅ Replaced using Approach 2');
      return true;
    }

    // Approach 3: Look for the group name with any surrounding div structure
    // Pattern: <div class="row"[^>]*>Button Group1</div>
    const regex3 = new RegExp(`<div class="row"[^>]*>\\s*${escapedGroupName}\\s*</div>`, 'g');
    if (regex3.test(this.renderedOutput)) {
      this.renderedOutput = this.renderedOutput.replace(regex3, groupHtml);
      console.log('[📦 BUTTON GROUP] ✅ Replaced using Approach 3');
      return true;
    }

    // Approach 4: Split by lines and check each line
    const lines = this.renderedOutput.split('\n');
    let found = false;
    for (let i = 0; i < lines.length; i++) {
      const trimmedLine = lines[i].trim();
      if (trimmedLine === groupName || trimmedLine.includes(groupName)) {
        // Check if this line is already inside a div
        if (lines[i].includes('<div')) {
          // Replace the entire line
          lines[i] = groupHtml;
        } else {
          // Wrap the text in the group HTML
          lines[i] = groupHtml;
        }
        found = true;
        console.log(`[📦 BUTTON GROUP] ✅ Found group at line ${i}: "${trimmedLine}"`);
        break;
      }
    }

    if (found) {
      this.renderedOutput = lines.join('\n');
      console.log('[📦 BUTTON GROUP] ✅ Replaced using Approach 4 (line-by-line)');
      return true;
    }

    // Approach 5: Fallback - replace the group name anywhere in the output
    if (this.renderedOutput.includes(groupName)) {
      this.renderedOutput = this.renderedOutput.replace(groupName, groupHtml);
      console.log('[📦 BUTTON GROUP] ✅ Replaced using Approach 5 (fallback)');
      return true;
    }

    console.log('[📦 BUTTON GROUP] ⚠️ Could not find group text in rendered output');
    console.log('[📦 BUTTON GROUP] 🔍 Rendered output preview:', this.renderedOutput.substring(0, 500));
    return true; // Still return true to not break the flow
  }

  // ============================================================
  // APPLY BUTTON GROUP TRANSFORMATION
  // ============================================================
 

  // ============================================================
  // APPLY STYLE TRANSFORMATION - UPDATED with multi-select & alignment
  // ============================================================
  applyStyleTransformationStep(step) {
    console.log('[TransformationPlayer] 🎨 applyStyleTransformationStep START');
    console.log('[TransformationPlayer] operation:', step.operation);
    console.log('[TransformationPlayer] operations:', step.operations);
    console.log('[TransformationPlayer] style_meta:', step.style_meta);

    const selectors = this.getSelectorsFromStep(step);
    if (selectors.length === 0) {
      console.log('[TransformationPlayer] ❌ No selectors found');
      return false;
    }

    // Extract operations and style_meta
    let operations = [];
    if (step.operations && Array.isArray(step.operations) && step.operations.length > 0) {
      operations = step.operations;
    } else if (step.operation) {
      operations = [step.operation];
    } else {
      operations = ['highlight'];
    }

    const styleMeta = step.style_meta || { alignment: 'left', full_row: false };
    const alignment = styleMeta.alignment || 'left';

    console.log('[TransformationPlayer] 📊 Operations to apply:', operations);
    console.log('[TransformationPlayer] 📊 Alignment:', alignment);
    console.log('[TransformationPlayer] 📊 Style meta:', styleMeta);

    const parser = new DOMParser();
    const doc = parser.parseFromString(this.renderedOutput, 'text/html');
    const screens = doc.querySelectorAll('.item-ui-screen');

    if (screens.length === 0) {
      console.log('[TransformationPlayer] ⚠️ No item-ui-screen found');
      return false;
    }

    let totalElementsModified = 0;
    let result = this.renderedOutput;

    for (const selector of selectors) {
      const startPath = selector.start || '';
      const endPath = selector.end || '';
      const loopSelector = selector.loopSelector || '';

      if (!startPath || !endPath) {
        console.log('[TransformationPlayer] ⚠️ Invalid selector, skipping');
        continue;
      }

      console.log('[TransformationPlayer] 🔍 Processing selector:');
      console.log('[TransformationPlayer]   startPath:', startPath);
      console.log('[TransformationPlayer]   endPath:', endPath);
      console.log('[TransformationPlayer]   loopSelector:', loopSelector);

      // Find the container for element lookup
      let containerForPath = screens[0];
      let adjustedStartPath = startPath;
      let adjustedEndPath = endPath;

      const contentWraps = screens[0].querySelector('.contentWraps');

      // If there's a loopSelector, find the loop container first
      if (loopSelector) {
        console.log('[TransformationPlayer] 🔄 Loop selector detected:', loopSelector);

        let loopContainer = null;
        let adjustedLoopSelector = loopSelector;

        if (contentWraps) {
          loopContainer = findElementByPathLocal(contentWraps, loopSelector);
          if (!loopContainer) {
            loopContainer = findElementByPathLocal(screens[0], loopSelector);
          }
          if (loopContainer) {
            containerForPath = loopContainer;
            console.log('[TransformationPlayer] ✅ Found loop container');
          } else {
            console.log('[TransformationPlayer] ⚠️ Could not find loop container, falling back to contentWraps');
            containerForPath = contentWraps || screens[0];
          }
        } else {
          containerForPath = screens[0];
        }
      } else {
        // No loop selector - adjust paths relative to contentWraps
        if (contentWraps) {
          const contentWrapsPath = getNthChildPathLocal(contentWraps, screens[0]);
          const startPathParts = startPath.split(' > ');
          const wrapperParts = contentWrapsPath ? contentWrapsPath.split(' > ') : [];

          if (wrapperParts.length > 0 && startPathParts.length >= wrapperParts.length) {
            let isPrefix = true;
            for (let i = 0; i < wrapperParts.length && i < startPathParts.length; i++) {
              if (startPathParts[i] !== wrapperParts[i]) {
                isPrefix = false;
                break;
              }
            }

            if (isPrefix) {
              let partsToStrip = wrapperParts.length;
              if (startPathParts.length > wrapperParts.length) {
                const nextPart = startPathParts[wrapperParts.length];
                const lastWrapperPart = wrapperParts[wrapperParts.length - 1];
                if (nextPart === lastWrapperPart) {
                  partsToStrip = wrapperParts.length + 1;
                }
              }
              adjustedStartPath = startPathParts.slice(partsToStrip).join(' > ');
              containerForPath = contentWraps;
              console.log('[TransformationPlayer] 🔄 Adjusted start path to:', adjustedStartPath);

              const endPathParts = endPath.split(' > ');
              if (endPathParts.length >= partsToStrip) {
                adjustedEndPath = endPathParts.slice(partsToStrip).join(' > ');
                console.log('[TransformationPlayer] 🔄 Adjusted end path to:', adjustedEndPath);
              }
            }
          }
        }
      }

      // Find the elements using the container and paths
      let startElement = null;
      let endElement = null;

      if (containerForPath) {
        startElement = findElementByPathLocal(containerForPath, adjustedStartPath);
        endElement = findElementByPathLocal(containerForPath, adjustedEndPath);
      }

      if ((!startElement || !endElement) && loopSelector && containerForPath !== screens[0]) {
        console.log('[TransformationPlayer] 🔄 Trying with loop container parent');
        const parentContainer = containerForPath.parentElement;
        if (parentContainer) {
          startElement = findElementByPathLocal(parentContainer, adjustedStartPath);
          endElement = findElementByPathLocal(parentContainer, adjustedEndPath);
          if (startElement && endElement) {
            containerForPath = parentContainer;
            console.log('[TransformationPlayer] ✅ Found elements using loop container parent');
          }
        }
      }

      if ((!startElement || !endElement) && contentWraps) {
        console.log('[TransformationPlayer] 🔄 Trying with contentWraps');
        startElement = findElementByPathLocal(contentWraps, adjustedStartPath);
        endElement = findElementByPathLocal(contentWraps, adjustedEndPath);
        if (startElement && endElement) {
          containerForPath = contentWraps;
          console.log('[TransformationPlayer] ✅ Found elements using contentWraps');
        }
      }

      if (!startElement || !endElement) {
        console.log('[TransformationPlayer] 🔄 Trying with screen');
        startElement = findElementByPathLocal(screens[0], startPath);
        endElement = findElementByPathLocal(screens[0], endPath);
        if (startElement && endElement) {
          containerForPath = screens[0];
          console.log('[TransformationPlayer] ✅ Found elements using screen');
        }
      }

      if (!startElement || !endElement) {
        console.log('[TransformationPlayer] ❌ Could not find elements for selector');
        continue;
      }

      console.log('[TransformationPlayer] ✅ Found elements:');
      console.log('[TransformationPlayer]   start:', startElement.tagName, startElement.className);
      console.log('[TransformationPlayer]   end:', endElement.tagName, endElement.className);

      // Get the selected text from the elements
      let selectedText = '';
      const elementsBetween = this.getElementsBetween(startElement, endElement, containerForPath);

      if (elementsBetween.length === 0) {
        selectedText = startElement.textContent?.trim() || '';
        if (startElement !== endElement) {
          selectedText += '\n' + (endElement.textContent?.trim() || '');
        }
      } else {
        for (const el of elementsBetween) {
          const elText = el.textContent?.trim() || '';
          if (elText) selectedText += elText + '\n';
        }
      }

      if (!selectedText) {
        selectedText = startElement.textContent?.trim() || '';
      }

      console.log('[TransformationPlayer] 📝 Selected text:', selectedText.substring(0, 100));

      // Build selectors for applyTransformationLogic
      const startPathForLogic = getNthChildPathLocal(startElement, containerForPath);
      const endPathForLogic = getNthChildPathLocal(endElement, containerForPath);

      const selectorsForLogic = [{
        start: { elementPath: startPathForLogic, nodeType: 1 },
        end: { elementPath: endPathForLogic, nodeType: 1 },
        ...(loopSelector ? { loopSelector: loopSelector } : {})
      }];

      console.log('[TransformationPlayer] 📍 startPathForLogic:', startPathForLogic);
      console.log('[TransformationPlayer] 📍 endPathForLogic:', endPathForLogic);

      // Apply the transformation using accumulated result
      const logicResult = applyTransformationLogic(
        selectedText,
        operations[0],
        false,
        result,
        this.template,
        this.currentData,
        screens[0],
        null,
        [],
        this.isSampleArray,
        'array',
        'vertical',
        'vertical',
        {
          selectors: selectorsForLogic,
          operations: operations,
          style_meta: styleMeta,
        }
      );

      if (!logicResult.success) {
        console.log('[TransformationPlayer] ❌ Style transformation failed:', logicResult.error);
        continue;
      }

      if (!logicResult.newPreview || logicResult.newPreview === result) {
        console.log('[TransformationPlayer] ⚠️ Style transformation resulted in no change');
        continue;
      }

      result = logicResult.newPreview;
      totalElementsModified += logicResult.elementsModified || 0;
      console.log(`[TransformationPlayer] ✅ Style transformation applied, modified ${logicResult.elementsModified || 0} elements`);
    }

    if (totalElementsModified === 0) {
      console.log('[TransformationPlayer] ❌ No elements were modified');
      return false;
    }

    this.renderedOutput = result;
    return true;
  }

  // ============================================================
  // APPLY STRUCTURAL TRANSFORMATION - FIXED to directly replace loop content
  // ============================================================
  applyStructuralTransformationStep(step) {
    console.log('[TransformationPlayer] 📐 applyStructuralTransformationStep START');
    console.log('[TransformationPlayer] operation:', step.operation);

    const selectors = this.getSelectorsFromStep(step);
    if (selectors.length === 0) {
      console.log('[TransformationPlayer] ❌ No selectors found');
      return false;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(this.renderedOutput, 'text/html');
    const screens = doc.querySelectorAll('.item-ui-screen');

    if (screens.length === 0) {
      console.log('[TransformationPlayer] ⚠️ No item-ui-screen found');
      return false;
    }

    let totalElementsModified = 0;
    let result = this.renderedOutput;

    for (const selector of selectors) {
      const startPath = selector.start || '';
      const endPath = selector.end || '';
      const loopSelector = selector.loopSelector || '';

      if (!startPath || !endPath) {
        console.log('[TransformationPlayer] ⚠️ Invalid selector, skipping');
        continue;
      }

      console.log('[TransformationPlayer] 🔍 Processing structural selector:');
      console.log('[TransformationPlayer]   startPath:', startPath);
      console.log('[TransformationPlayer]   endPath:', endPath);
      console.log('[TransformationPlayer]   loopSelector:', loopSelector);

      for (let screenIdx = 0; screenIdx < screens.length; screenIdx++) {
        const screen = screens[screenIdx];
        const screenIndex = screen.getAttribute('data-item-ui-index') || screenIdx;

        const contentWraps = screen.querySelector('.contentWraps');

        if (loopSelector) {
          console.log('[TransformationPlayer] 🔄 Loop selector detected:', loopSelector);

          let loopContainer = null;
          let adjustedLoopSelector = loopSelector;

          if (contentWraps) {
            const contentWrapsPath = getNthChildPathLocal(contentWraps, screen);
            const loopParts = loopSelector.split(' > ');
            const wrapperParts = contentWrapsPath ? contentWrapsPath.split(' > ') : [];

            if (wrapperParts.length > 0 && loopParts.length >= wrapperParts.length) {
              let isPrefix = true;
              for (let i = 0; i < wrapperParts.length && i < loopParts.length; i++) {
                if (loopParts[i] !== wrapperParts[i]) {
                  isPrefix = false;
                  break;
                }
              }
              if (isPrefix) {
                let partsToStrip = wrapperParts.length;
                if (loopParts.length > wrapperParts.length) {
                  const nextPart = loopParts[wrapperParts.length];
                  const lastWrapperPart = wrapperParts[wrapperParts.length - 1];
                  if (nextPart === lastWrapperPart) {
                    partsToStrip = wrapperParts.length + 1;
                  }
                }
                adjustedLoopSelector = loopParts.slice(partsToStrip).join(' > ');
                loopContainer = findElementByPathLocal(contentWraps, adjustedLoopSelector);
              }
            }
          }

          if (!loopContainer) {
            loopContainer = findElementByPathLocal(screen, loopSelector);
          }

          if (!loopContainer && contentWraps) {
            loopContainer = findElementByPathLocal(contentWraps, loopSelector);
          }

          if (!loopContainer) {
            console.log(`[TransformationPlayer] ⚠️ Could not find loop container in screen ${screenIndex}`);
            continue;
          }

          let itemsPerIteration = parseInt(loopContainer.getAttribute('data-items-per-iteration')) || 0;
          if (!itemsPerIteration || itemsPerIteration === 0) {
            console.log('[TransformationPlayer] ⚠️ No items-per-iteration found, using default 4');
            itemsPerIteration = 4;
          }

          const allRows = loopContainer.querySelectorAll('.row');
          const allRowsArray = Array.from(allRows);
          console.log(`[TransformationPlayer] 📊 Found ${allRowsArray.length} rows in loop`);

          const startPathParts = startPath.split(' > ');
          const endPathParts = endPath.split(' > ');

          let startRowIndex = -1;
          let endRowIndex = -1;
          let startChildPath = '';
          let endChildPath = '';

          if (startPathParts.length > 0) {
            const rowMatch = startPathParts[0].match(/div:nth-child\((\d+)\)/);
            if (rowMatch) {
              startRowIndex = parseInt(rowMatch[1]) - 1;
              startChildPath = startPathParts.slice(1).join(' > ');
            }
          }

          if (endPathParts.length > 0) {
            const rowMatch = endPathParts[0].match(/div:nth-child\((\d+)\)/);
            if (rowMatch) {
              endRowIndex = parseInt(rowMatch[1]) - 1;
              endChildPath = endPathParts.slice(1).join(' > ');
            }
          }

          if (startRowIndex === -1 || endRowIndex === -1) {
            console.log('[TransformationPlayer] ⚠️ Could not parse row indices');
            continue;
          }

          const startPosition = startRowIndex % itemsPerIteration;
          const endPosition = endRowIndex % itemsPerIteration;
          console.log(`[TransformationPlayer] 📍 Start position: ${startPosition}, End position: ${endPosition}`);

          const selectedRows = [];
          const selectedElements = [];
          let rawDataText = '';

          for (let i = 0; i < allRowsArray.length; i++) {
            const positionInIteration = i % itemsPerIteration;

            let isInRange = false;
            if (startPosition <= endPosition) {
              isInRange = positionInIteration >= startPosition && positionInIteration <= endPosition;
            } else {
              isInRange = positionInIteration >= startPosition || positionInIteration <= endPosition;
            }

            if (isInRange) {
              const row = allRowsArray[i];
              selectedRows.push(row);

              const rowText = row.textContent?.trim() || '';
              if (rowText && !row.classList.contains('empty-line')) {
                rawDataText += rowText + '\n';
              }

              let startEl = null;
              let endEl = null;

              if (startChildPath) {
                startEl = findElementByPathLocal(row, startChildPath);
              }
              if (!startEl) {
                startEl = row.querySelector('.key, .value, .separator');
              }
              if (!startEl) {
                startEl = row;
              }

              if (endChildPath && endChildPath !== startChildPath) {
                endEl = findElementByPathLocal(row, endChildPath);
              }
              if (!endEl) {
                endEl = startEl;
              }

              const elementsBetween = this.getElementsBetween(startEl, endEl, row);
              if (elementsBetween.length > 0) {
                selectedElements.push(...elementsBetween);
              } else {
                selectedElements.push(startEl);
              }
            }
          }

          console.log(`[TransformationPlayer] 📊 Selected ${selectedRows.length} rows and ${selectedElements.length} elements`);
          console.log(`[TransformationPlayer] 📝 Raw data text: ${rawDataText.substring(0, 200)}...`);

          if (selectedElements.length === 0) {
            console.log('[TransformationPlayer] ⚠️ No elements selected for structural transformation');
            continue;
          }

          const parsedData = parseRowsToData(rawDataText);
          console.log('[TransformationPlayer] 📊 Parsed data:', parsedData);

          let transformedContent = '';
          let generatedHtml = '';

          switch (step.operation) {
            case 'table':
              generatedHtml = generateTableHTML(parsedData || []);
              break;
            case 'cards':
              generatedHtml = generateCardHTML(parsedData || []);
              break;
            case 'list':
              generatedHtml = generateListHTML(parsedData || []);
              break;
            case 'json':
              generatedHtml = JSON.stringify(parsedData || [], null, 2);
              break;
            default:
              generatedHtml = JSON.stringify(parsedData || [], null, 2);
          }

          transformedContent = generatedHtml;
          console.log(`[TransformationPlayer] 📊 Generated ${step.operation} HTML:`, transformedContent.substring(0, 200));

          if (!transformedContent) {
            console.log('[TransformationPlayer] ⚠️ Could not generate transformed content');
            continue;
          }

          const loopContainerHtml = loopContainer.outerHTML;
          const loopContainerOpenTag = loopContainerHtml.substring(0, loopContainerHtml.indexOf('>') + 1);

          const newLoopContainerHtml = `${loopContainerOpenTag}${transformedContent}</div>`;

          const tempDiv = doc.createElement('div');
          tempDiv.innerHTML = newLoopContainerHtml;
          const newLoopContainer = tempDiv.firstChild;

          if (newLoopContainer) {
            loopContainer.parentNode.replaceChild(newLoopContainer, loopContainer);
            totalElementsModified++;
            console.log('[TransformationPlayer] ✅ Replaced loop container with transformed content');
          }

        } else {
          console.log('[TransformationPlayer] 📍 No loop selector - single structural');

          let containerForPath = screen;
          let adjustedStartPath = startPath;
          let adjustedEndPath = endPath;

          if (contentWraps) {
            const contentWrapsPath = getNthChildPathLocal(contentWraps, screen);
            const startPathParts = startPath.split(' > ');
            const wrapperParts = contentWrapsPath ? contentWrapsPath.split(' > ') : [];

            if (wrapperParts.length > 0 && startPathParts.length >= wrapperParts.length) {
              let isPrefix = true;
              for (let i = 0; i < wrapperParts.length && i < startPathParts.length; i++) {
                if (startPathParts[i] !== wrapperParts[i]) {
                  isPrefix = false;
                  break;
                }
              }

              if (isPrefix) {
                let partsToStrip = wrapperParts.length;
                if (startPathParts.length > wrapperParts.length) {
                  const nextPart = startPathParts[wrapperParts.length];
                  const lastWrapperPart = wrapperParts[wrapperParts.length - 1];
                  if (nextPart === lastWrapperPart) {
                    partsToStrip = wrapperParts.length + 1;
                  }
                }
                adjustedStartPath = startPathParts.slice(partsToStrip).join(' > ');
                containerForPath = contentWraps;

                const endPathParts = endPath.split(' > ');
                if (endPathParts.length >= partsToStrip) {
                  adjustedEndPath = endPathParts.slice(partsToStrip).join(' > ');
                }
              }
            }
          }

          let startElement = findElementByPathLocal(containerForPath, adjustedStartPath);
          let endElement = findElementByPathLocal(containerForPath, adjustedEndPath);

          if (!startElement || !endElement) {
            startElement = findElementByPathLocal(containerForPath, startPath);
            endElement = findElementByPathLocal(containerForPath, endPath);
          }

          if (!startElement || !endElement) {
            startElement = findElementByPathLocal(screen, startPath);
            endElement = findElementByPathLocal(screen, endPath);
          }

          if (!startElement || !endElement) {
            console.log('[TransformationPlayer] ❌ Could not find elements for selector');
            continue;
          }

          let selectedText = '';
          const elementsBetween = this.getElementsBetween(startElement, endElement, containerForPath);

          if (elementsBetween.length === 0) {
            selectedText = startElement.textContent?.trim() || '';
            if (startElement !== endElement) {
              selectedText += '\n' + (endElement.textContent?.trim() || '');
            }
          } else {
            for (const el of elementsBetween) {
              const elText = el.textContent?.trim() || '';
              if (elText) selectedText += elText + '\n';
            }
          }

          if (!selectedText) {
            selectedText = startElement.textContent?.trim() || '';
          }

          const parsedData = parseRowsToData(selectedText);
          let transformedContent = '';

          switch (step.operation) {
            case 'table':
              transformedContent = generateTableHTML(parsedData || []);
              break;
            case 'cards':
              transformedContent = generateCardHTML(parsedData || []);
              break;
            case 'list':
              transformedContent = generateListHTML(parsedData || []);
              break;
            case 'json':
              transformedContent = JSON.stringify(parsedData || [], null, 2);
              break;
            default:
              transformedContent = JSON.stringify(parsedData || [], null, 2);
          }

          if (transformedContent) {
            const tempDiv = doc.createElement('div');
            tempDiv.innerHTML = transformedContent;

            const parent = startElement.parentElement;
            const elementsToReplace = [];
            let current = startElement;
            while (current && current !== endElement.nextElementSibling) {
              elementsToReplace.push(current);
              current = current.nextElementSibling;
            }

            for (const el of elementsToReplace) {
              el.parentNode.removeChild(el);
            }

            while (tempDiv.firstChild) {
              parent.insertBefore(tempDiv.firstChild, parent.children[0] || null);
            }

            totalElementsModified++;
            console.log('[TransformationPlayer] ✅ Replaced elements with transformed content');
          }
        }
      }
    }

    if (totalElementsModified === 0) {
      console.log('[TransformationPlayer] ❌ No elements were modified');
      return false;
    }

    const body = doc.body;
    let serialized = '';
    for (const child of body.children) {
      serialized += child.outerHTML;
    }

    this.renderedOutput = serialized;
    console.log(`[TransformationPlayer] 📐 Structural transformation applied, modified ${totalElementsModified} elements`);
    return true;
  }

  // ============================================================
  // APPLY MEDIA TRANSFORMATION - FIXED for all iterations in loop
  // ============================================================
  applyMediaTransformationStep(step) {
    console.log('[TransformationPlayer] 🖼️ applyMediaTransformationStep START');
    console.log('[TransformationPlayer] operation:', step.operation);

    const selectors = this.getSelectorsFromStep(step);
    if (selectors.length === 0) {
      console.log('[TransformationPlayer] ❌ No selectors found');
      return false;
    }

    const imageWidth = step.imageSize?.width || step._metadata?.imageWidth || 250;
    const imageHeight = step.imageSize?.height || step._metadata?.imageHeight || 250;
    const preserveAspectRatio = step.imageSize?.preserveAspectRatio !== undefined
      ? step.imageSize.preserveAspectRatio
      : true;

    const parser = new DOMParser();
    const doc = parser.parseFromString(this.renderedOutput, 'text/html');
    const screens = doc.querySelectorAll('.item-ui-screen');

    if (screens.length === 0) {
      console.log('[TransformationPlayer] ⚠️ No item-ui-screen found');
      return false;
    }

    let totalReplacedCount = 0;

    const createImageHtml = (url, width, height, preserveRatio) => {
      const cleanUrl = url.replace(/"/g, "&quot;");
      const objectFit = preserveRatio ? 'cover' : 'fill';
      return `<div class="media-wrapper" style="display:inline-block;margin:4px 0;">
      <img 
        src="${cleanUrl}" 
        alt="Image" 
        class="preview-image rendered-image" 
        style="width:${width}px;height:${height}px;object-fit:${objectFit};border-radius:8px;border:1px solid #e2e8f0;max-width:100%;box-shadow:0 2px 4px rgba(0,0,0,0.1);" 
        loading="lazy" 
        onerror="this.style.display='none';this.parentElement.innerHTML='<div style=\\'color:#a0aec0;font-size:12px;\\'>🖼️ Image unavailable</div>';"
      />
    </div>`;
    };

    for (const selector of selectors) {
      const startPath = selector.start || '';
      const endPath = selector.end || '';
      const loopSelector = selector.loopSelector || '';

      console.log('[TransformationPlayer] 🔍 Processing media selector:');
      console.log('[TransformationPlayer]   startPath:', startPath);
      console.log('[TransformationPlayer]   endPath:', endPath);
      console.log('[TransformationPlayer]   loopSelector:', loopSelector);

      if (!startPath || !endPath) {
        console.log('[TransformationPlayer] ⚠️ Invalid selector, skipping');
        continue;
      }

      for (let screenIdx = 0; screenIdx < screens.length; screenIdx++) {
        const screen = screens[screenIdx];
        const screenIndex = screen.getAttribute('data-item-ui-index') || screenIdx;

        const contentWraps = screen.querySelector('.contentWraps');

        if (loopSelector) {
          console.log('[TransformationPlayer] 🔄 Loop selector detected:', loopSelector);

          let loopContainer = null;
          let adjustedLoopSelector = loopSelector;

          if (contentWraps) {
            const contentWrapsPath = getNthChildPathLocal(contentWraps, screen);
            const loopParts = loopSelector.split(' > ');
            const wrapperParts = contentWrapsPath ? contentWrapsPath.split(' > ') : [];

            if (wrapperParts.length > 0 && loopParts.length >= wrapperParts.length) {
              let isPrefix = true;
              for (let i = 0; i < wrapperParts.length && i < loopParts.length; i++) {
                if (loopParts[i] !== wrapperParts[i]) {
                  isPrefix = false;
                  break;
                }
              }
              if (isPrefix) {
                let partsToStrip = wrapperParts.length;
                if (loopParts.length > wrapperParts.length) {
                  const nextPart = loopParts[wrapperParts.length];
                  const lastWrapperPart = wrapperParts[wrapperParts.length - 1];
                  if (nextPart === lastWrapperPart) {
                    partsToStrip = wrapperParts.length + 1;
                  }
                }
                adjustedLoopSelector = loopParts.slice(partsToStrip).join(' > ');
                loopContainer = findElementByPathLocal(contentWraps, adjustedLoopSelector);
                console.log('[TransformationPlayer] ✅ Found loop container via contentWraps');
              }
            }
          }

          if (!loopContainer) {
            loopContainer = findElementByPathLocal(screen, loopSelector);
            if (loopContainer) {
              console.log('[TransformationPlayer] ✅ Found loop container via screen');
            }
          }

          if (!loopContainer && contentWraps) {
            loopContainer = findElementByPathLocal(contentWraps, loopSelector);
            if (loopContainer) {
              console.log('[TransformationPlayer] ✅ Found loop container via contentWraps direct');
            }
          }

          if (!loopContainer) {
            console.log(`[TransformationPlayer] ⚠️ Could not find loop container in screen ${screenIndex}`);
            continue;
          }

          let itemsPerIteration = parseInt(loopContainer.getAttribute('data-items-per-iteration')) || 0;
          if (!itemsPerIteration || itemsPerIteration === 0) {
            console.log('[TransformationPlayer] ⚠️ No items-per-iteration found, using default 4');
            itemsPerIteration = 4;
          }

          const allRows = loopContainer.querySelectorAll('.row');
          const allRowsArray = Array.from(allRows);
          console.log(`[TransformationPlayer] 📊 Found ${allRowsArray.length} rows in loop`);

          const pathParts = startPath.split(' > ');
          let targetRowIndex = -1;
          let targetChildPath = '';

          if (pathParts.length > 0) {
            const rowMatch = pathParts[0].match(/div:nth-child\((\d+)\)/);
            if (rowMatch) {
              const rowNth = parseInt(rowMatch[1]);
              targetRowIndex = rowNth - 1;
              targetChildPath = pathParts.slice(1).join(' > ');
              console.log(`[TransformationPlayer] 📍 Target row index: ${targetRowIndex}, child path: ${targetChildPath}`);
            }
          }

          if (targetRowIndex === -1) {
            console.log('[TransformationPlayer] ⚠️ Could not parse target row index from path');
            continue;
          }

          const targetPositionInIteration = targetRowIndex % itemsPerIteration;
          console.log(`[TransformationPlayer] 📍 Target position in iteration: ${targetPositionInIteration}`);

          let screenReplacedCount = 0;

          for (let i = 0; i < allRowsArray.length; i++) {
            const positionInIteration = i % itemsPerIteration;

            if (positionInIteration === targetPositionInIteration) {
              const row = allRowsArray[i];

              let targetElement = null;

              if (targetChildPath) {
                targetElement = findElementByPathLocal(row, targetChildPath);
              }

              if (!targetElement) {
                targetElement = row.querySelector('.value.image-url');
              }

              if (!targetElement) {
                targetElement = row.querySelector('[data-image-url]');
              }

              if (targetElement) {
                let imageUrl = null;
                let elementToReplace = null;

                if (targetElement.getAttribute('data-image-url')) {
                  imageUrl = targetElement.getAttribute('data-image-url');
                  elementToReplace = targetElement;
                } else if (targetElement.classList?.contains('value') && targetElement.classList?.contains('image-url')) {
                  imageUrl = targetElement.textContent?.trim() || '';
                  elementToReplace = targetElement;
                } else {
                  const imageSpan = targetElement.querySelector?.('.value.image-url');
                  if (imageSpan) {
                    imageUrl = imageSpan.textContent?.trim() || '';
                    elementToReplace = imageSpan;
                  }
                  if (!elementToReplace) {
                    const dataImageElement = targetElement.querySelector?.('[data-image-url]');
                    if (dataImageElement) {
                      imageUrl = dataImageElement.getAttribute('data-image-url') || dataImageElement.textContent?.trim() || '';
                      elementToReplace = dataImageElement;
                    }
                  }
                }

                if (elementToReplace && imageUrl && isImageUrl(imageUrl)) {
                  const imgHtml = createImageHtml(imageUrl, imageWidth, imageHeight, preserveAspectRatio);
                  const tempDiv = doc.createElement('div');
                  tempDiv.innerHTML = imgHtml;
                  const newElement = tempDiv.firstChild;

                  if (newElement) {
                    elementToReplace.parentNode.replaceChild(newElement, elementToReplace);
                    screenReplacedCount++;
                    console.log(`[TransformationPlayer] 🖼️ Replaced image at row ${i}, position ${positionInIteration} (${imageWidth}×${imageHeight})`);
                  }
                }
              }
            }
          }

          totalReplacedCount += screenReplacedCount;
          console.log(`[TransformationPlayer] 🖼️ Screen ${screenIndex} - Replaced ${screenReplacedCount} images`);

        } else {
          console.log('[TransformationPlayer] 📍 No loop selector - single image');

          let containerForPath = screen;
          let adjustedStartPath = startPath;

          if (contentWraps) {
            const contentWrapsPath = getNthChildPathLocal(contentWraps, screen);
            const startPathParts = startPath.split(' > ');
            const wrapperParts = contentWrapsPath ? contentWrapsPath.split(' > ') : [];

            if (wrapperParts.length > 0 && startPathParts.length >= wrapperParts.length) {
              let isPrefix = true;
              for (let i = 0; i < wrapperParts.length && i < startPathParts.length; i++) {
                if (startPathParts[i] !== wrapperParts[i]) {
                  isPrefix = false;
                  break;
                }
              }

              if (isPrefix) {
                let partsToStrip = wrapperParts.length;
                if (startPathParts.length > wrapperParts.length) {
                  const nextPart = startPathParts[wrapperParts.length];
                  const lastWrapperPart = wrapperParts[wrapperParts.length - 1];
                  if (nextPart === lastWrapperPart) {
                    partsToStrip = wrapperParts.length + 1;
                  }
                }
                adjustedStartPath = startPathParts.slice(partsToStrip).join(' > ');
                containerForPath = contentWraps;
                console.log('[TransformationPlayer] 🔄 Adjusted path to:', adjustedStartPath);
              }
            }
          }

          let targetElement = findElementByPathLocal(containerForPath, adjustedStartPath);

          if (!targetElement && contentWraps) {
            targetElement = findElementByPathLocal(contentWraps, startPath);
            if (targetElement) {
              containerForPath = contentWraps;
            }
          }

          if (!targetElement) {
            targetElement = findElementByPathLocal(screen, startPath);
            if (targetElement) {
              containerForPath = screen;
            }
          }

          if (!targetElement) {
            console.log(`[TransformationPlayer] ⚠️ Could not find target element in screen ${screenIndex}`);
            continue;
          }

          let imageUrl = null;
          let elementToReplace = null;

          if (targetElement.getAttribute('data-image-url')) {
            imageUrl = targetElement.getAttribute('data-image-url');
            elementToReplace = targetElement;
          } else if (targetElement.classList?.contains('value') && targetElement.classList?.contains('image-url')) {
            imageUrl = targetElement.textContent?.trim() || '';
            elementToReplace = targetElement;
          } else {
            const imageSpan = targetElement.querySelector?.('.value.image-url');
            if (imageSpan) {
              imageUrl = imageSpan.textContent?.trim() || '';
              elementToReplace = imageSpan;
            }
            if (!elementToReplace) {
              const dataImageElement = targetElement.querySelector?.('[data-image-url]');
              if (dataImageElement) {
                imageUrl = dataImageElement.getAttribute('data-image-url') || dataImageElement.textContent?.trim() || '';
                elementToReplace = dataImageElement;
              }
            }
          }

          if (!elementToReplace || !imageUrl) {
            console.log(`[TransformationPlayer] ⚠️ No image URL found in screen ${screenIndex}`);
            continue;
          }

          if (!isImageUrl(imageUrl)) {
            console.log(`[TransformationPlayer] ⚠️ Not a valid image URL: ${imageUrl.substring(0, 50)}...`);
            continue;
          }

          const imgHtml = createImageHtml(imageUrl, imageWidth, imageHeight, preserveAspectRatio);
          const tempDiv = doc.createElement('div');
          tempDiv.innerHTML = imgHtml;
          const newElement = tempDiv.firstChild;

          if (newElement) {
            elementToReplace.parentNode.replaceChild(newElement, elementToReplace);
            totalReplacedCount++;
            console.log(`[TransformationPlayer] 🖼️ Replaced image in screen ${screenIndex} (${imageWidth}×${imageHeight})`);
          }
        }
      }
    }

    if (totalReplacedCount === 0) {
      console.log('[TransformationPlayer] ⚠️ No images were replaced');
      return false;
    }

    const body = doc.body;
    let serialized = '';
    for (const child of body.children) {
      serialized += child.outerHTML;
    }

    this.renderedOutput = serialized;
    console.log(`[TransformationPlayer] 🖼️ Media transformation applied, replaced ${totalReplacedCount} images`);
    return true;
  }

  // ============================================================
  // APPLY COMBINED STEP
  // ============================================================
  applyCombinedStep(step) {
    console.log('[TransformationPlayer] 📋 applyCombinedStep START');
    console.log('[TransformationPlayer] combined step id:', step.id);

    if (!step.phases) {
      console.log('[TransformationPlayer] ⚠️ No phases in combined step');
      return false;
    }

    let success = true;

    if (step.phases.structural?.operations?.length > 0) {
      for (const op of step.phases.structural.operations) {
        const tempStep = {
          id: `${step.id}_structural_${op.operation}`,
          type: 'structural',
          operation: op.operation,
          fields: op.fields || [],
          selectors: step.selectors || []
        };
        const result = this.applyStructuralTransformationStep(tempStep);
        if (!result) success = false;
      }
    }

    if (step.phases.media?.operations?.length > 0) {
      for (const op of step.phases.media.operations) {
        const tempStep = {
          id: `${step.id}_media_${op.operation}`,
          type: 'media',
          operation: op.operation,
          fields: op.fields || [],
          selectors: step.selectors || [],
          imageSize: op.imageSize || { width: 250, height: 250 }
        };
        const result = this.applyMediaTransformationStep(tempStep);
        if (!result) success = false;
      }
    }

    if (step.phases.styles?.operations?.length > 0) {
      for (const op of step.phases.styles.operations) {
        const tempStep = {
          id: `${step.id}_styles_${op.operation}`,
          type: 'style',
          operation: op.operation || 'highlight',
          operations: op.operations || [op.operation || 'highlight'],
          style_meta: op.style_meta || { alignment: 'left', full_row: false },
          fields: op.fields || [],
          selectors: step.selectors || []
        };
        const result = this.applyStyleTransformationStep(tempStep);
        if (!result) success = false;
      }
    }

    return success;
  }

  // ============================================================
  // APPLY STEP - Main entry point
  // ============================================================
  applyStep(step) {
    console.log('[TransformationPlayer] 🔄 applyStep() called');
    console.log('[TransformationPlayer] operation:', step.operation);
    console.log('[TransformationPlayer] type:', step.type);

    if (!this.currentData) {
      this.setError('No data available');
      return null;
    }

    try {
      let success = false;

      if (this.isButtonStep(step)) {
        success = this.applyButtonTransformationStep(step);
        if (!success) {
          this.setError('Button transformation failed');
          return null;
        }
      } else if (this.isButtonGroupStep(step)) {
        success = this.applyButtonGroupTransformationStep(step);
        if (!success) {
          this.setError('Button group transformation failed');
          return null;
        }
      } else if (this.isMediaStep(step)) {
        success = this.applyMediaTransformationStep(step);
        if (!success) {
          this.setError('Media transformation failed or no changes made');
          return null;
        }
      } else if (this.isStyleStep(step)) {
        success = this.applyStyleTransformationStep(step);
        if (!success) {
          this.setError('Style transformation failed or no changes made');
          return null;
        }
      } else if (this.isStructuralStep(step)) {
        success = this.applyStructuralTransformationStep(step);
        if (!success) {
          this.setError('Structural transformation failed or no changes made');
          return null;
        }
      } else if (this.isCombinedStep(step)) {
        success = this.applyCombinedStep(step);
        if (!success) {
          this.setError('Combined transformation failed');
          return null;
        }
      } else {
        this.setError(`Unknown transformation type: ${step.type}`);
        return null;
      }

      const stepResult = {
        step: step,
        timestamp: new Date().toISOString(),
        success: true,
      };
      this.stepResults.push(stepResult);

      if (this.onStepComplete) {
        this.onStepComplete(stepResult, this.stepResults.length - 1);
      }

      return stepResult;
    } catch (err) {
      console.log('[TransformationPlayer] ❌ Error in applyStep:', err.message);
      console.log('[TransformationPlayer] ❌ Stack:', err.stack);
      this.setError(`Error applying step: ${err.message}`);
      return null;
    }
  }

  // ============================================================
  // PLAY METHODS
  // ============================================================
  playNext() {
    console.log('[TransformationPlayer] ⏭ playNext() called');
    if (this.currentStepIndex >= this.transformationSteps.length) {
      this.isPlaying = false;
      return;
    }
    const step = this.transformationSteps[this.currentStepIndex];
    const result = this.applyStep(step);
    if (result) {
      this.currentStepIndex++;
      this.render();
    }
  }

  async playAll() {
    console.log('[TransformationPlayer] ▶️ playAll() called');
    this.isPlaying = true;
    this.error = null;
    this.stepResults = [];
    this.currentStepIndex = 0;

    const parsed = typeof this.sampleResponse === 'string'
      ? JSON.parse(this.sampleResponse)
      : this.sampleResponse;

    this.currentData = parsed;
    this.isSampleArray = Array.isArray(parsed);
    this.caseType = this.detectCaseType(parsed);
    this.caseDescription = this.getCaseDescription(this.caseType);
    this.renderedOutput = this.renderTemplate(parsed);
    this.originalRenderedOutput = this.renderedOutput;
    this.render();

    for (let i = 0; i < this.transformationSteps.length; i++) {
      const step = this.transformationSteps[i];
      const result = this.applyStep(step);
      if (result) {
        this.currentStepIndex = i + 1;
        this.render();
        await new Promise(resolve => setTimeout(resolve, 300));
      } else {
        break;
      }
    }

    this.isPlaying = false;
  }

  reset() {
    console.log('[TransformationPlayer] 🔄 reset() called');
    this.currentStepIndex = 0;
    this.stepResults = [];
    this.error = null;
    const parsed = typeof this.sampleResponse === 'string'
      ? JSON.parse(this.sampleResponse)
      : this.sampleResponse;
    this.currentData = parsed;
    this.isSampleArray = Array.isArray(parsed);
    this.caseType = this.detectCaseType(parsed);
    this.caseDescription = this.getCaseDescription(this.caseType);
    this.renderedOutput = this.renderTemplate(parsed);
    this.originalRenderedOutput = this.renderedOutput;
    this.render();
  }

  setError(message) {
    console.log('[TransformationPlayer] ❌ setError:', message);
    this.error = message;
    if (this.onError) {
      this.onError(message);
    }
    this.render();
  }

  // ============================================================
  // RENDER
  // ============================================================
  render() {
    console.log('[TransformationPlayer] render() called');
    if (!this.targetElement) return;

    const container = this.targetElement;
    container.innerHTML = '';

    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;flex-wrap:wrap;';
    header.innerHTML = `
      <div style="display:flex;align-items:center;gap:12px;">
        <span style="font-weight:600;font-size:16px;color:#2d3748;">🔄 Transformation Player</span>
        <span style="font-size:13px;color:#718096;">Step ${this.currentStepIndex} of ${this.transformationSteps.length}</span>
        <span style="font-size:11px;color:#a0aec0;background:#edf2f7;padding:2px 8px;border-radius:10px;">${this.caseType || 'unknown'}</span>
        <span style="font-size:10px;color:#718096;background:#f7fafc;padding:2px 8px;border-radius:10px;max-width:300px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${this.caseDescription || ''}</span>
      </div>
      <div>
        ${this.isPlaying ?
        `<span style="padding:4px 12px;border-radius:12px;font-size:12px;font-weight:500;background:#ebf8ff;color:#2b6cb0;">⏳ Playing...</span>` :
        `<span style="padding:4px 12px;border-radius:12px;font-size:12px;font-weight:500;background:#edf2f7;color:#4a5568;">⏸ Idle</span>`
      }
      </div>
    `;
    container.appendChild(header);

    if (this.error) {
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = 'padding:8px 12px;background:#fed7d7;color:#9b2c2c;border-radius:4px;margin-bottom:12px;font-size:13px;';
      errorDiv.textContent = `❌ ${this.error}`;
      container.appendChild(errorDiv);
    }

    const stepsContainer = document.createElement('div');
    stepsContainer.style.cssText = 'margin-bottom:16px;';
    stepsContainer.innerHTML = `
      <div style="font-weight:600;font-size:14px;color:#4a5568;margin-bottom:8px;">Steps (${this.transformationSteps.length})</div>
      <div>
        ${this.transformationSteps.map((step, index) => {
      const status = index < this.currentStepIndex ? 'completed' :
        index === this.currentStepIndex && this.isPlaying ? 'playing' : 'pending';
      const statusStyles = status === 'completed' ? 'border-color:#48bb78;background:#f0fff4;' :
        status === 'playing' ? 'border-color:#4299e1;background:#ebf8ff;' :
          'opacity:0.6;';
      let typeLabel = step.type || 'Unknown';
      const typeLabels = {
        'button': '🔘 Button',
        'button-group': '📦 Button Group',
        'style': '🎨 Style',
        'structural': '📐 Structural',
        'media': '🖼️ Media',
        'combined': '📋 Combined'
      };
      typeLabel = typeLabels[typeLabel] || typeLabel;
      const selectorsCount = this.getSelectorsFromStep(step).length;
      const buttonText = step.buttonText || '';
      const groupName = step.groupName || '';

      let operationsDisplay = '';
      if (step.type === 'style' && step.operations && step.operations.length > 0) {
        operationsDisplay = `(${step.operations.join(', ')})`;
      }

      let alignmentDisplay = '';
      if (step.type === 'style' && step.style_meta?.alignment && step.style_meta.alignment !== 'left') {
        alignmentDisplay = `📐 ${step.style_meta.alignment}`;
      }

      return `
            <div style="display:flex;align-items:center;gap:10px;padding:6px 10px;background:white;border-radius:4px;border:1px solid #e2e8f0;font-size:12px;margin-bottom:4px;transition:all 0.2s;${statusStyles}">
              <span style="font-weight:600;color:#4a5568;min-width:40px;">#${index + 1}</span>
              <span style="padding:1px 8px;border-radius:10px;font-size:10px;font-weight:600;text-transform:uppercase;background:#edf2f7;color:#4a5568;">${typeLabel}</span>
              <span style="font-weight:500;color:#2d3748;min-width:50px;">${step.operation || ''}</span>
              ${operationsDisplay ? `<span style="color:#2b6cb0;font-size:11px;font-family:monospace;">${operationsDisplay}</span>` : ''}
              ${alignmentDisplay ? `<span style="color:#2b6cb0;font-size:10px;background:#ebf8ff;padding:1px 6px;border-radius:8px;">${alignmentDisplay}</span>` : ''}
              ${buttonText ? `<span style="color:#718096;font-size:11px;font-family:monospace;">${buttonText}</span>` : ''}
              ${groupName ? `<span style="color:#718096;font-size:11px;font-family:monospace;">${groupName}</span>` : ''}
              ${step.fields && step.fields.length > 0 ? `<span style="color:#718096;font-size:11px;font-family:monospace;">${step.fields.join(', ')}</span>` : ''}
              ${selectorsCount > 1 ? `<span style="font-size:10px;color:#a0aec0;">${selectorsCount} selectors</span>` : ''}
              <span style="margin-left:auto;">${status === 'completed' ? '✅' : status === 'playing' ? '⏳' : ''}</span>
            </div>
          `;
    }).join('')}
      </div>
    `;
    container.appendChild(stepsContainer);

    const preview = document.createElement('div');
    preview.style.cssText = 'margin-bottom:16px;';
    preview.innerHTML = `<div style="font-weight:600;font-size:14px;color:#4a5568;margin-bottom:8px;">Rendered Output</div>`;

    const outputDiv = document.createElement('div');
    outputDiv.style.cssText = 'background:#f7fafc;border-radius:8px;border:2px solid #e2e8f0;padding:16px;min-height:300px;max-height:400px;overflow:auto;';
    outputDiv.innerHTML = this.renderPreviewContent(this.renderedOutput);
    preview.appendChild(outputDiv);
    container.appendChild(preview);

    if (this.stepResults.length > 0) {
      const results = document.createElement('div');
      results.innerHTML = `
        <div style="font-weight:600;font-size:14px;color:#4a5568;margin-bottom:8px;">Results (${this.stepResults.length})</div>
        <div>
          ${this.stepResults.map((result, index) => {
        const step = result.step;
        let opsDisplay = '';
        if (step.type === 'style' && step.operations && step.operations.length > 0) {
          opsDisplay = ` (${step.operations.join(', ')})`;
        }
        let alignDisplay = '';
        if (step.type === 'style' && step.style_meta?.alignment && step.style_meta.alignment !== 'left') {
          alignDisplay = ` 📐${step.style_meta.alignment}`;
        }
        return `
            <div style="display:flex;align-items:center;gap:10px;padding:4px 10px;background:white;border-radius:4px;border:1px solid #e2e8f0;font-size:12px;margin-bottom:4px;">
              <span style="font-weight:600;color:#4a5568;">Step ${index + 1}</span>
              <span style="font-weight:500;color:#2d3748;">${step.operation || 'unknown'}${opsDisplay}${alignDisplay}</span>
              <span style="color:#48bb78;margin-left:auto;">✅ Applied</span>
            </div>
          `;
      }).join('')}
        </div>
      `;
      container.appendChild(results);
    }
  }

  renderPreviewContent(htmlContent) {
    if (!htmlContent) {
      return '<div style="padding:16px;color:#a0aec0;">No content to display</div>';
    }

    const hasItemUIScreens = htmlContent.includes('class="item-ui-screen"');

    if (hasItemUIScreens) {
      return `<div style="flex:1;background-color:#f7fafc;border-radius:8px;border:2px solid #e2e8f0;padding:16px;min-height:300px;max-height:100%;overflow:auto;">${htmlContent}</div>`;
    }

    const containerStyle = 'flex:1;background-color:#f7fafc;border-radius:8px;border:2px solid #e2e8f0;padding:16px;min-height:300px;max-height:100%;overflow:auto;';

    return `
<div style="${containerStyle}">
  <div class="item-ui-screen" data-item-ui-index="0">
    <div><div class="contentWraps">
      <div style="white-space:pre-wrap;word-break:break-word;font-family:monospace;font-size:14px;line-height:1.8;">
        ${htmlContent}
      </div>
    </div></div>
  </div>
</div>`;
  }

  update(options = {}) {
    console.log('[TransformationPlayer] update() called');
    if (options.template !== undefined) this.template = options.template;
    if (options.sampleResponse !== undefined) {
      this.sampleResponse = options.sampleResponse;
      this.init();
    }
    if (options.transformationSteps !== undefined) {
      this.transformationSteps = options.transformationSteps;
      this.reset();
    }
    if (options.targetElement !== undefined) {
      this.targetElement = options.targetElement;
      this.render();
    }
  }

  destroy() {
    console.log('[TransformationPlayer] destroy() called');
    if (this.targetElement) {
      this.targetElement.innerHTML = '';
    }
    this.isPlaying = false;
    this._nunjucksEnv = null;
  }
}

export default TransformationPlayer;